// Asks before leaving the canvas throws unsaved changes away. Needed since nothing saves by itself
// (2026-10-01): until then, leaving could never lose more than the last 1.2 s.
//
// Four ways out are covered: a reload or closed tab (the browser's own prompt), a click on any
// in-app link (the sidebar, the account menu), the canvas's own Back buttons, which call the
// returned function, and anything else that navigates in code and asks `mayLeaveCanvas()` first
// (the Ctrl+K palette). NOT covered: the browser's Back button. The app runs on <BrowserRouter>,
// and React Router can only block that navigation under a data router.
import { useCallback, useEffect } from "react";

export const LEAVE_MESSAGE =
  "This automation has unsaved changes. Leave without saving them?";

// The question to ask while a canvas holds unsaved work; null when there is none.
let activeGuard: (() => boolean) | null = null;

/**
 * For code that navigates without a link (the Ctrl+K palette): true when it is fine to leave --
 * nothing unsaved on an open canvas, or the operator said so.
 */
export function mayLeaveCanvas(): boolean {
  return activeGuard ? activeGuard() : true;
}

/** Returns `confirmLeave()`: true when it is fine to leave (nothing unsaved, or the operator said so). */
export function useLeaveGuard(active: boolean): () => boolean {
  useEffect(() => {
    if (!active) return;

    function onBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
      // Older browsers read the prompt from here; the text itself is never shown any more.
      event.returnValue = "";
    }

    // Capture phase on the document: runs before React Router's own click handler on the link,
    // so a "No" stops the navigation before it starts.
    function onClick(event: MouseEvent) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target === "_blank" || anchor.hasAttribute("download")) return;
      const url = new URL(anchor.href, window.location.href);
      // Another site unloads the page, and the browser's own prompt (beforeunload) asks then --
      // asking here as well asked twice. A mailto: or tel: link (origin "null") opens an app and
      // leaves the page where it is.
      if (url.origin !== window.location.origin) return;
      // Same page (a ?run= or ?enrolment= view): nothing is unmounted, nothing is lost.
      if (url.pathname === window.location.pathname) return;
      if (!window.confirm(LEAVE_MESSAGE)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    const ask = () => window.confirm(LEAVE_MESSAGE);
    activeGuard = ask;
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onClick, true);
    return () => {
      if (activeGuard === ask) activeGuard = null;
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, [active]);

  return useCallback(() => !active || window.confirm(LEAVE_MESSAGE), [active]);
}
